/**
 * What a discount comes to, in integer paise.
 *
 * 🔴 THIS IS THE BROWSER HALF OF `pricing.ts` + `discount.ts`, AND IT IS A
 * QUOTE RATHER THAN A CHARGE. Nothing here decides what a school is asked for —
 * that is `SubscriptionRow.netChargePaise`, computed on the server from the
 * school's own plan and its live enrolment. This answers a different question,
 * asked at a different moment: *"if I offer 12% off, what does a school of
 * ninety children actually pay?"*, on the call where that is being decided.
 *
 * 🔴 THE REASON IT EXISTS AT ALL IS THAT THE TWO NUMBERS USED TO BE COMPUTED BY
 * TWO PEOPLE, DAYS APART. An operator writing a coupon had no way to see what it
 * came to until a school claimed it, so the figure quoted on the phone and the
 * figure on the invoice were two separate pieces of arithmetic — and the first
 * one was done in somebody's head.
 *
 * 🔴 INTEGER PAISE THROUGHOUT, NEVER A FLOAT. `billing.ts` states the rule and
 * the number that proves it: `Number('19.99') * 100` is 1998.9999999999998, which
 * loses a paisa per student per month. Every value in and out of this file is a
 * whole number of paise, and the only decimal point in the product is in
 * `formatPaise`.
 *
 * ⚠️ IT DUPLICATES THE SERVER'S ARITHMETIC, WHICH IS A REAL COST AND IS TAKEN
 * DELIBERATELY. The alternative is a "preview this coupon" endpoint, which would
 * be a route that prices a school that has not claimed anything — and the shape
 * `billing.ts` warns about (*"an amount in this request is the one shape that
 * turns a checkout into a discount form"*) is exactly what a client-supplied
 * seat count and rate would be. So the duplication is confined to this file, it
 * is pure, and it is testable; what it must never do is reach a write.
 */

export interface QuoteInput {
  perStudentPaise: number;
  floorPaise: number;
  /** How many children. Anything unreadable is treated as none. */
  seats: number;
  /** A whole percentage, or `null`. Exactly one of the two is used. */
  percentOff: number | null;
  /** A decimal string of rupees — `"500"`, `"499.50"` — or `null`. */
  amountOff: string | null;
  /**
   * 🔴 A DECIMAL STRING OF RUPEES **PER ENROLLED CHILD**, or `null` — change 19
   * §16's kind. It is a separate field from `amountOff` rather than a flag
   * beside it so that `offOf` cannot read one as the other; on a ninety-child
   * school the two answers differ by a factor of ninety, and both look like
   * plausible money on a screen.
   */
  perStudentOff: string | null;
}

export interface Quote {
  /** Seats × rate, or the plan's floor, whichever is larger. */
  grossPaise: number;
  discountPaise: number;
  netPaise: number;
  perStudentPaise: number;
  floorPaise: number;
  /**
   * 🔴 WHETHER THE FLOOR DECIDED THE LIST PRICE, IN THE ANSWER. A small school's
   * gross is the plan's minimum rather than seats × rate, and an operator who
   * does not know that will read the figure as a bug in the calculator.
   */
  floorApplied: boolean;
}

/**
 * ⚠️ EXACTLY ONE OF THE THREE DISCOUNT FIELDS IS READ, IN THE ORDER `offOf`
 * CHECKS THEM. The server refuses a coupon with two of them set, so a quote for
 * one is a quote for something that cannot exist.
 *
 * ⚠️ A MALFORMED DISCOUNT IS **NO** DISCOUNT, NEVER A GUESSED ONE. `discount.ts`
 * on the server takes the identical position and gives the reason: this is
 * arithmetic that must never hand money away by accident, so an unreadable
 * percentage answers zero rather than something plausible. The screen then says
 * "this takes nothing off", which is true and is visible.
 */
export function quote(input: QuoteInput): Quote {
  const seats = Number.isFinite(input.seats) && input.seats > 0 ? Math.floor(input.seats) : 0;
  const perStudentPaise = Math.max(0, Math.round(input.perStudentPaise));
  const floorPaise = Math.max(0, Math.round(input.floorPaise));

  const bySeats = seats * perStudentPaise;
  const grossPaise = Math.max(bySeats, floorPaise);
  const floorApplied = floorPaise > bySeats;

  const discountPaise = Math.min(grossPaise, offOf(grossPaise, input));

  return {
    grossPaise,
    discountPaise,
    /* ⚠️ NEVER BELOW ZERO. A flat amount larger than the bill is a free term,
       not a credit — the server clamps identically, and a negative figure on a
       screen would be read as money owed back. */
    netPaise: grossPaise - discountPaise,
    perStudentPaise,
    floorPaise,
    floorApplied,
  };
}

function offOf(grossPaise: number, input: QuoteInput): number {
  if (input.percentOff !== null) {
    const pct = input.percentOff;
    if (!Number.isFinite(pct) || pct <= 0) return 0;
    /*
     * 🔴 ROUNDED, NOT TRUNCATED, AND ROUNDED ONCE. `Math.round` on the paise
     * keeps the quote within half a paisa of the server's own figure; flooring
     * would drift a rupee on a large school, which is exactly the size of
     * discrepancy that makes somebody distrust the whole screen.
     */
    return Math.round((grossPaise * Math.min(pct, 100)) / 100);
  }
  if (input.amountOff !== null) return rupeesToPaise(input.amountOff);
  if (input.perStudentOff !== null) {
    /* ⚠️ THE SEAT COUNT IS RE-DERIVED HERE RATHER THAN PASSED DOWN, and it is
       the same clamp `quote` applies: a school with no readable enrolment gets
       no discount, never a one-child one. */
    const seats = Number.isFinite(input.seats) && input.seats > 0 ? Math.floor(input.seats) : 0;
    return rupeesToPaise(input.perStudentOff) * seats;
  }
  return 0;
}

/**
 * `"499.50"` → `49950`.
 *
 * 🔴 PARSED AS TWO INTEGERS RATHER THAN MULTIPLIED AS A FLOAT, which is the
 * whole point of the file's header. `Number('19.99') * 100` is
 * 1998.9999999999998; splitting on the point and padding the fraction to two
 * digits cannot lose a paisa.
 *
 * ⚠️ THE EXAMPLE USED TO BE `'8.115'`, WHICH IS NOT ONE. That value is exactly
 * 811.5 as a double and rounds UP to 812 — it demonstrates the opposite of the
 * hazard, and this function rejects three-decimal input anyway. `19.99`, `0.29`
 * and `70.07` are the two-decimal amounts that genuinely drift low, and
 * `discountModel.test.ts` asserts all three against the naive form.
 *
 * ⚠️ ANYTHING UNREADABLE IS ZERO. See `offOf` — a malformed discount is no
 * discount.
 */
export function rupeesToPaise(value: string): number {
  const text = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return 0;
  const [whole, fraction = ""] = text.split(".");
  return Number.parseInt(whole, 10) * 100 + Number.parseInt(fraction.padEnd(2, "0"), 10);
}
